import * as React from "react";
import { useFinance } from "@/context/FinanceContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, eachMonthOfInterval, subMonths, subYears } from "date-fns";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ptBR } from "date-fns/locale";
import { Header } from "@/components/Header";
import { DollarSign, BarChart3, Calendar, Sparkles, X, Filter, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Line, LineChart, Legend, LabelList } from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { HorizontalRevenueBarChart } from "@/components/HorizontalRevenueBarChart";
import { ReportTransactionsTable } from "@/components/ReportTransactionsTable";
import { DateInput } from "@/components/ui/date-input";
import { Label } from "@/components/ui/label";
import { ExportReportPdfButton } from "@/components/ExportReportPdfButton";

const currency = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL"
});
// Cor verde para receitas
const REVENUE_COLOR = "#22c55e";

function VariationBadge({ current, previous, label, invertColor = false }: { current: number; previous: number; label: string; invertColor?: boolean }) {
  if (previous === 0 && current === 0) return <span className="text-xs text-muted-foreground">— {label}</span>;
  if (previous === 0) return <span className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="h-3 w-3 text-emerald-500" /> novo {label}</span>;
  const pct = ((current - previous) / previous) * 100;
  const isPositive = pct > 0;
  const isNeutral = pct === 0;
  // For revenue: positive = good (green). invertColor flips for expenses.
  const colorClass = isNeutral ? "text-muted-foreground" : (isPositive && !invertColor) || (!isPositive && invertColor) ? "text-emerald-600" : "text-red-500";
  const Icon = isNeutral ? Minus : isPositive ? TrendingUp : TrendingDown;
  return (
    <span className={`text-xs flex items-center gap-1 ${colorClass}`}>
      <Icon className="h-3 w-3" />
      {isPositive ? "+" : ""}{pct.toFixed(0)}% {label}
    </span>
  );
}

export default function RelatorioVendas() {
  const {
    transactions,
    categories,
    accounts,
    paymentMethods,
    costCenters,
    suppliers
  } = useFinance();
  const currentDate = new Date();
  const [startDate, setStartDate] = React.useState<Date>(startOfMonth(currentDate));
  const [endDate, setEndDate] = React.useState<Date>(endOfMonth(currentDate));
  const [statusFilter, setStatusFilter] = React.useState<"all" | "settled" | "pending">("all");
  const [dateField, setDateField] = React.useState<"issueDate" | "dueDate" | "paymentDate">("issueDate");
  const [selectedFilter, setSelectedFilter] = React.useState<{
    type: 'category' | 'account' | 'payment' | 'date';
    name: string;
    value?: string;
  } | null>(null);
  const [evolutionGrouping, setEvolutionGrouping] = React.useState<'day' | 'month'>('day');

  React.useEffect(() => {
    document.title = "Relatório de Receitas | Radar Financeiro";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Análise completa de receitas com métricas detalhadas, distribuição e evolução temporal.');
  }, []);

  // Helper to filter transactions by type, period, status
  const filterByPeriod = React.useCallback((start: Date, end: Date) => {
    return transactions.filter(t => {
      if (t.type !== "receita") return false;
      if (t.kind === "transferencia") return false;
      const dateValue = t[dateField];
      if (!dateValue) return false;
      const parsedDate = parseISO(dateValue);
      const inPeriod = parsedDate >= start && parsedDate <= end;
      if (!inPeriod) return false;
      if (statusFilter === "settled") return t.settled === true;
      if (statusFilter === "pending") return t.settled !== true;
      return true;
    });
  }, [transactions, statusFilter, dateField]);

  // Filtrar transações de receita do período selecionado (excluindo transferências)
  const periodTransactions = React.useMemo(() => filterByPeriod(startDate, endDate), [filterByPeriod, startDate, endDate]);

  // Previous month and previous year totals
  const prevMonthTotal = React.useMemo(() => {
    const pStart = subMonths(startDate, 1);
    const pEnd = subMonths(endDate, 1);
    return filterByPeriod(pStart, pEnd).reduce((sum, t) => sum + t.value, 0);
  }, [filterByPeriod, startDate, endDate]);

  const prevYearTotal = React.useMemo(() => {
    const pStart = subYears(startDate, 1);
    const pEnd = subYears(endDate, 1);
    return filterByPeriod(pStart, pEnd).reduce((sum, t) => sum + t.value, 0);
  }, [filterByPeriod, startDate, endDate]);

  const prevMonthSettled = React.useMemo(() => {
    const pStart = subMonths(startDate, 1);
    const pEnd = subMonths(endDate, 1);
    return filterByPeriod(pStart, pEnd).filter(t => t.settled).reduce((sum, t) => sum + t.value, 0);
  }, [filterByPeriod, startDate, endDate]);

  const prevYearSettled = React.useMemo(() => {
    const pStart = subYears(startDate, 1);
    const pEnd = subYears(endDate, 1);
    return filterByPeriod(pStart, pEnd).filter(t => t.settled).reduce((sum, t) => sum + t.value, 0);
  }, [filterByPeriod, startDate, endDate]);

  const prevMonthPending = React.useMemo(() => {
    const pStart = subMonths(startDate, 1);
    const pEnd = subMonths(endDate, 1);
    return filterByPeriod(pStart, pEnd).filter(t => !t.settled).reduce((sum, t) => sum + t.value, 0);
  }, [filterByPeriod, startDate, endDate]);

  const prevYearPending = React.useMemo(() => {
    const pStart = subYears(startDate, 1);
    const pEnd = subYears(endDate, 1);
    return filterByPeriod(pStart, pEnd).filter(t => !t.settled).reduce((sum, t) => sum + t.value, 0);
  }, [filterByPeriod, startDate, endDate]);

  // Filtered transactions based on chart/table click
  const filteredTransactions = React.useMemo(() => {
    if (!selectedFilter) return periodTransactions;
    return periodTransactions.filter(t => {
      if (selectedFilter.type === 'category') {
        const cat = categories.find(c => c.id === t.categoryId);
        return (cat?.name || "Sem Categoria") === selectedFilter.name;
      }
      if (selectedFilter.type === 'account') {
        const acc = accounts.find(a => a.id === t.accountId);
        return (acc?.name || "Sem Conta") === selectedFilter.name;
      }
      if (selectedFilter.type === 'payment') {
        const pm = paymentMethods.find(p => p.id === t.paymentMethodId);
        return (pm?.name || "Sem Forma de Pagamento") === selectedFilter.name;
      }
      if (selectedFilter.type === 'date') {
        const dateValue = t[dateField];
        if (!dateValue) return false;
        return format(parseISO(dateValue), 'dd/MM') === selectedFilter.value;
      }
      return true;
    });
  }, [periodTransactions, selectedFilter, categories, accounts, paymentMethods, dateField]);

  // 1. VISÃO GERAL
  const overview = React.useMemo(() => {
    const totalReceita = filteredTransactions.reduce((sum, t) => sum + t.value, 0);
    const receitaRealizada = filteredTransactions.filter(t => t.settled).reduce((sum, t) => sum + t.value, 0);
    const receitaARealizar = filteredTransactions.filter(t => !t.settled).reduce((sum, t) => sum + t.value, 0);
    return {
      total: totalReceita,
      realizada: receitaRealizada,
      aRealizar: receitaARealizar
    };
  }, [filteredTransactions]);

  // Distribution totals always use periodTransactions (unfiltered)
  const distributionTotal = React.useMemo(() => {
    return periodTransactions.reduce((sum, t) => sum + t.value, 0);
  }, [periodTransactions]);

  // 2. DISTRIBUIÇÃO POR CATEGORIA
  const revenueByCategory = React.useMemo(() => {
    const categoryMap: Record<string, { name: string; value: number; count: number; color: string; }> = {};
    periodTransactions.forEach(t => {
      const category = categories.find(c => c.id === t.categoryId);
      const categoryName = category?.name || "Sem Categoria";
      const categoryColor = category?.color || "#64748b";
      if (!categoryMap[t.categoryId]) {
        categoryMap[t.categoryId] = { name: categoryName, value: 0, count: 0, color: categoryColor };
      }
      categoryMap[t.categoryId].value += t.value;
      categoryMap[t.categoryId].count += 1;
    });
    return Object.values(categoryMap).sort((a, b) => b.value - a.value);
  }, [periodTransactions, categories]);

  // 2. DISTRIBUIÇÃO POR CONTA FINANCEIRA
  const revenueByAccount = React.useMemo(() => {
    const accountMap: Record<string, { name: string; value: number; count: number; }> = {};
    periodTransactions.forEach(t => {
      const account = accounts.find(a => a.id === t.accountId);
      const accountName = account?.name || "Sem Conta";
      if (!accountMap[t.accountId]) {
        accountMap[t.accountId] = { name: accountName, value: 0, count: 0 };
      }
      accountMap[t.accountId].value += t.value;
      accountMap[t.accountId].count += 1;
    });
    return Object.values(accountMap).sort((a, b) => b.value - a.value);
  }, [periodTransactions, accounts]);

  // 2. DISTRIBUIÇÃO POR FORMA DE PAGAMENTO
  const revenueByPaymentMethod = React.useMemo(() => {
    const methodMap: Record<string, { name: string; value: number; count: number; }> = {};
    periodTransactions.forEach(t => {
      const method = paymentMethods.find(p => p.id === t.paymentMethodId);
      const methodName = method?.name || "Sem Forma de Pagamento";
      if (!methodMap[t.paymentMethodId || 'none']) {
        methodMap[t.paymentMethodId || 'none'] = { name: methodName, value: 0, count: 0 };
      }
      methodMap[t.paymentMethodId || 'none'].value += t.value;
      methodMap[t.paymentMethodId || 'none'].count += 1;
    });
    return Object.values(methodMap).sort((a, b) => b.value - a.value);
  }, [periodTransactions, paymentMethods]);

  // 3. EVOLUÇÃO (diária ou mensal)
  const dailyEvolution = React.useMemo(() => {
    if (evolutionGrouping === 'month') {
      const months = eachMonthOfInterval({ start: startOfMonth(startDate), end: endOfMonth(endDate) });
      return months.map(month => {
        const monthStr = format(month, 'yyyy-MM');
        const monthTransactions = filteredTransactions.filter(t => {
          const dateValue = t[dateField];
          if (!dateValue) return false;
          return format(parseISO(dateValue), 'yyyy-MM') === monthStr;
        });
        const total = monthTransactions.reduce((sum, t) => sum + t.value, 0);
        return { date: format(month, 'MMM/yy', { locale: ptBR }), fullDate: monthStr, value: total, count: monthTransactions.length };
      });
    }
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    return days.map(day => {
      const dayStr = format(day, 'yyyy-MM-dd');
      const dayTransactions = filteredTransactions.filter(t => {
        const dateValue = t[dateField];
        if (!dateValue) return false;
        return format(parseISO(dateValue), 'yyyy-MM-dd') === dayStr;
      });
      const total = dayTransactions.reduce((sum, t) => sum + t.value, 0);
      return { date: format(day, 'dd/MM'), fullDate: dayStr, value: total, count: dayTransactions.length };
    });
  }, [filteredTransactions, startDate, endDate, dateField, evolutionGrouping]);

  const handleChartItemClick = React.useCallback((type: 'category' | 'account' | 'payment') => (name: string) => {
    if (!name) {
      setSelectedFilter(null);
    } else {
      setSelectedFilter({ type, name });
    }
  }, []);

  const handleTabChange = React.useCallback(() => {
    setSelectedFilter(null);
  }, []);

  const handleLineChartClick = React.useCallback((data: any) => {
    if (evolutionGrouping === 'month') return;
    if (data?.activePayload?.[0]) {
      const clickedDate = data.activePayload[0].payload.date;
      setSelectedFilter(prev => {
        if (prev?.type === 'date' && prev.value === clickedDate) return null;
        return { type: 'date', name: `Data: ${clickedDate}`, value: clickedDate };
      });
    }
  }, [evolutionGrouping]);

  const handleRowClick = React.useCallback((transaction: any) => {
    const cat = categories.find(c => c.id === transaction.categoryId);
    const catName = cat?.name || "Sem Categoria";
    setSelectedFilter(prev => {
      if (prev?.type === 'category' && prev.name === catName) return null;
      return { type: 'category', name: catName };
    });
  }, [categories]);

  const reportRef = React.useRef<HTMLDivElement>(null);

  return <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <Header />
      <div ref={reportRef} className="page-bleed py-6 space-y-6">
        {/* Cabeçalho */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent flex items-center gap-2">
              <Sparkles className="h-8 w-8 text-primary" />
              Relatório de Receitas
            </h1>
            <p className="text-muted-foreground mt-2">Análise completa e detalhada das suas receitas</p>
          </div>
          <ExportReportPdfButton
            targetRef={reportRef}
            title="Relatório de Receitas"
            subtitle={`Período: ${format(startDate, "dd/MM/yyyy")} até ${format(endDate, "dd/MM/yyyy")}`}
            filenameSlug="receitas"
          />
        </div>


        {/* Filtros */}
        <Card className="border-2 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-4">
              <div className="space-y-2 w-full sm:w-auto">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Data Inicial
                </Label>
                <DateInput
                  value={format(startDate, 'yyyy-MM-dd')}
                  onChange={(value) => value && setStartDate(parseISO(value))}
                  className="h-11 w-full sm:w-[160px]"
                />
              </div>
              <div className="space-y-2 w-full sm:w-auto">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Data Final
                </Label>
                <DateInput
                  value={format(endDate, 'yyyy-MM-dd')}
                  onChange={(value) => value && setEndDate(parseISO(value))}
                  className="h-11 w-full sm:w-[160px]"
                />
              </div>
              <div className="space-y-2 w-full sm:w-auto">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  Filtrar por
                </Label>
                <Select value={dateField} onValueChange={(value: "issueDate" | "dueDate" | "paymentDate") => setDateField(value)}>
                  <SelectTrigger className="h-11 w-full sm:w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="issueDate">Emissão</SelectItem>
                    <SelectItem value="dueDate">Vencimento</SelectItem>
                    <SelectItem value="paymentDate">Pagamento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 w-full sm:w-auto">
                <label className="text-sm font-semibold">Status</label>
                <Select value={statusFilter} onValueChange={(value: "all" | "settled" | "pending") => setStatusFilter(value)}>
                  <SelectTrigger className="h-11 w-full sm:w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Completo</SelectItem>
                    <SelectItem value="settled">Realizado</SelectItem>
                    <SelectItem value="pending">A Realizar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active filter indicator */}
        {selectedFilter && (
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Badge variant="secondary" className="text-sm px-3 py-1 gap-2">
              Filtrado por: {selectedFilter.name}
              <X
                className="h-3.5 w-3.5 cursor-pointer hover:text-destructive transition-colors"
                onClick={() => setSelectedFilter(null)}
              />
            </Badge>
          </div>
        )}

        {/* 1. VISÃO GERAL - Cards Premium */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Receita Total */}
          <Card className="relative overflow-hidden border-2 shadow-lg hover:shadow-xl transition-all hover:scale-105">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-transparent" />
            <CardHeader className="relative pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Receita Total
              </CardTitle>
            </CardHeader>
            <CardContent className="relative">
              <div className="text-3xl font-bold">{currency.format(overview.total)}</div>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-muted-foreground">{filteredTransactions.length} transações</span>
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                <VariationBadge current={overview.total} previous={prevMonthTotal} label="vs mês ant." />
                <VariationBadge current={overview.total} previous={prevYearTotal} label="vs ano ant." />
              </div>
            </CardContent>
          </Card>

          {/* Receita Realizada */}
          <Card className="relative overflow-hidden border-2 shadow-lg hover:shadow-xl transition-all hover:scale-105">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 via-transparent to-transparent" />
            <CardHeader className="relative pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-success" />
                Receita Realizada
              </CardTitle>
            </CardHeader>
            <CardContent className="relative">
              <div className="text-3xl font-bold text-success">{currency.format(overview.realizada)}</div>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="bg-success/10 text-success border-success/20">
                  {overview.total > 0 ? (overview.realizada / overview.total * 100).toFixed(0) : 0}%
                </Badge>
                <span className="text-xs text-muted-foreground">do total</span>
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                <VariationBadge current={overview.realizada} previous={prevMonthSettled} label="vs mês ant." />
                <VariationBadge current={overview.realizada} previous={prevYearSettled} label="vs ano ant." />
              </div>
            </CardContent>
          </Card>

          {/* Receita a Realizar */}
          <Card className="relative overflow-hidden border-2 shadow-lg hover:shadow-xl transition-all hover:scale-105">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-transparent" />
            <CardHeader className="relative pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Calendar className="h-4 w-4 text-orange-500" />
                A Realizar
              </CardTitle>
            </CardHeader>
            <CardContent className="relative">
              <div className="text-3xl font-bold text-orange-600">{currency.format(overview.aRealizar)}</div>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="bg-orange-500/10 text-orange-600 border-orange-500/20">
                  {overview.total > 0 ? (overview.aRealizar / overview.total * 100).toFixed(0) : 0}%
                </Badge>
                <span className="text-xs text-muted-foreground">do total</span>
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                <VariationBadge current={overview.aRealizar} previous={prevMonthPending} label="vs mês ant." />
                <VariationBadge current={overview.aRealizar} previous={prevYearPending} label="vs ano ant." />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 3. EVOLUÇÃO NO TEMPO */}
        <Card className="border-2 shadow-lg">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <BarChart3 className="h-6 w-6 text-primary" />
                  {evolutionGrouping === 'month' ? 'Evolução Mensal das Receitas' : 'Evolução Diária das Receitas'}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {format(startDate, "dd 'de' MMMM", { locale: ptBR })} até {format(endDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  {evolutionGrouping === 'day' && " · Clique em um ponto para filtrar"}
                </p>
              </div>
              <ToggleGroup
                type="single"
                value={evolutionGrouping}
                onValueChange={(v) => v && setEvolutionGrouping(v as 'day' | 'month')}
                className="shrink-0"
              >
                <ToggleGroupItem value="day" size="sm" aria-label="Diário">Diário</ToggleGroupItem>
                <ToggleGroupItem value="month" size="sm" aria-label="Mensal">Mensal</ToggleGroupItem>
              </ToggleGroup>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyEvolution} margin={{ top: 20, right: 30, left: 20, bottom: 20 }} onClick={handleLineChartClick} style={{ cursor: 'pointer' }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" tickFormatter={value => `${(value / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} formatter={(value: number) => [currency.format(value), 'Receita']} labelFormatter={label => `Data: ${label}`} />
                  <Legend />
                  <Line type="monotone" dataKey="value" name={evolutionGrouping === 'month' ? 'Receita Mensal' : 'Receita Diária'} stroke={REVENUE_COLOR} strokeWidth={3} dot={{ fill: REVENUE_COLOR, r: 4 }} activeDot={{ r: 8, strokeWidth: 2, stroke: 'hsl(var(--background))' }}>
                    <LabelList dataKey="value" position="top" offset={10} formatter={(v: number) => v > 0 ? Math.round(v).toString() : ''} style={{ fontSize: 11, fill: 'hsl(var(--foreground))', fontWeight: 600 }} />
                  </Line>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* 2. DISTRIBUIÇÃO DA RECEITA - Tabs */}
        <Card className="border-2 shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-primary" />
              Distribuição da Receita
            </CardTitle>
            <p className="text-sm text-muted-foreground">Clique em uma linha para filtrar todo o relatório</p>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="category" className="space-y-6" onValueChange={handleTabChange}>
              <TabsList className="grid w-full grid-cols-3 h-12">
                <TabsTrigger value="category" className="text-base">Por Categoria</TabsTrigger>
                <TabsTrigger value="account" className="text-base">Por Conta Financeira</TabsTrigger>
                <TabsTrigger value="payment" className="text-base">Por Forma de Pagamento</TabsTrigger>
              </TabsList>

              <TabsContent value="category" className="space-y-4">
                <div className="flex items-center gap-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b pb-2">
                  <div className="w-40 shrink-0">Categoria</div>
                  <div className="flex-1 text-center">Barra</div>
                  <div className="w-28 shrink-0 text-right">Valor</div>
                  <div className="w-16 shrink-0 text-right">%</div>
                  <div className="w-12 shrink-0 text-right">Qtd</div>
                </div>
                <HorizontalRevenueBarChart 
                  data={revenueByCategory} 
                  total={distributionTotal}
                  barColor={REVENUE_COLOR}
                  onItemClick={handleChartItemClick('category')}
                  selectedItem={selectedFilter?.type === 'category' ? selectedFilter.name : null}
                />
              </TabsContent>

              <TabsContent value="account" className="space-y-4">
                <div className="flex items-center gap-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b pb-2">
                  <div className="w-40 shrink-0">Conta Financeira</div>
                  <div className="flex-1 text-center">Barra</div>
                  <div className="w-28 shrink-0 text-right">Valor</div>
                  <div className="w-16 shrink-0 text-right">%</div>
                  <div className="w-12 shrink-0 text-right">Qtd</div>
                </div>
                <HorizontalRevenueBarChart 
                  data={revenueByAccount} 
                  total={distributionTotal}
                  barColor={REVENUE_COLOR}
                  onItemClick={handleChartItemClick('account')}
                  selectedItem={selectedFilter?.type === 'account' ? selectedFilter.name : null}
                />
               </TabsContent>
 
               <TabsContent value="payment" className="space-y-4">
                 <div className="flex items-center gap-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b pb-2">
                   <div className="w-40 shrink-0">Forma de Pagamento</div>
                   <div className="flex-1 text-center">Barra</div>
                   <div className="w-28 shrink-0 text-right">Valor</div>
                   <div className="w-16 shrink-0 text-right">%</div>
                   <div className="w-12 shrink-0 text-right">Qtd</div>
                 </div>
                 <HorizontalRevenueBarChart 
                  data={revenueByPaymentMethod} 
                  total={distributionTotal}
                  barColor={REVENUE_COLOR}
                  onItemClick={handleChartItemClick('payment')}
                  selectedItem={selectedFilter?.type === 'payment' ? selectedFilter.name : null}
                 />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Tabela de Detalhamento */}
        <ReportTransactionsTable
          transactions={filteredTransactions}
          categories={categories}
          accounts={accounts}
          paymentMethods={paymentMethods}
          costCenters={costCenters}
          suppliers={suppliers}
          accentColor={REVENUE_COLOR}
          title="Detalhamento dos Lançamentos"
          onRowClick={handleRowClick}
        />

      </div>
    </div>;
}
